import React from 'react';
import { motion } from 'framer-motion';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import CountingNumber from '../CountingNumber';
import { EloData } from '../../types/api.types';

interface PlayerPerformanceStatsProps {
  playerData: EloData;
  currentLang: 'en' | 'fr' | 'de' | 'it';
  t: {
    performanceStats: string;
    basedOnLast12Months: string;
    totalGames: string;
    eloChange: string;
    overallWinRate: string;
    vsHigherElo: string;
    vsLowerElo: string;
  };
}

/**
 * PlayerPerformanceStats Component
 * Displays performance statistics including games played, win rates, and ELO changes
 */
const PlayerPerformanceStats: React.FC<PlayerPerformanceStatsProps> = ({
  playerData,
  currentLang,
  t
}) => {
  const stats = playerData?.stats;

  // Don't render if stats are not available
  if (!stats) {
    return null;
  }

  return (
    <div className="max-w-6xl mx-auto mb-16">
      {/* Section Header */}
      <motion.div
        className="mb-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 1.2 }}>
        <div className="text-center mb-10">
          {/* Lottie above title */}
          <motion.div
            className="flex justify-center mb-4"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 1.3 }}
          >
            <div style={{ width: '220px', height: '220px' }}>
              <DotLottieReact
                src="/rankings.lottie"
                loop
                autoplay
                style={{ width: '100%', height: '100%' }}
              />
            </div>
          </motion.div>
          <h2 className="text-4xl font-light text-gray-900 dark:text-gray-100 mb-3 transition-colors duration-300">{t.performanceStats}</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">{t.basedOnLast12Months}</p>
        </div>
      </motion.div>

      {/* Top Row - 2 Columns on Mobile, 3 on Desktop: Total Games, ELO Change, Overall Win Rate */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
        {/* Total Games Card */}
        <motion.div
          className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg transition-colors duration-300"
          initial={{ opacity: 0, scale: 0.9, y: 0 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.3, type: "spring", stiffness: 100 }}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center transition-colors duration-300">
              <svg className="w-5 h-5 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
          </div>
          <div className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2 transition-colors duration-300">{t.totalGames}</div>
          <div className="text-5xl font-bold text-gray-900 dark:text-gray-100 transition-colors duration-300">
            <CountingNumber target={stats.total_games} duration={2} delay={600} decimals={0} />
          </div>
        </motion.div>

        {/* ELO Change Card */}
        <motion.div
          className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg transition-colors duration-300"
          initial={{ opacity: 0, scale: 0.9, y: 0 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.4, type: "spring", stiffness: 100 }}>
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors duration-300 ${
              (playerData.stats?.elo_change_30d || 0) >= 0
                ? 'bg-[#059669]/10 dark:bg-[#059669]/20'
                : 'bg-[#C5283D]/10 dark:bg-[#C5283D]/20'
            }`}>
              <svg className={`w-5 h-5 ${
                (playerData.stats?.elo_change_30d || 0) >= 0
                  ? 'text-[#059669]'
                  : 'text-[#C5283D]'
              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {(playerData.stats?.elo_change_30d || 0) >= 0 ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                )}
              </svg>
            </div>
          </div>
          <div className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2 transition-colors duration-300">{t.eloChange}</div>
          <div className={`text-5xl font-bold transition-colors duration-300 ${
            (playerData.stats?.elo_change_30d || 0) >= 0
              ? 'text-[#059669]'
              : 'text-[#C5283D]'
          }`}>
            <CountingNumber
              target={playerData.stats?.elo_change_30d || 0}
              duration={2}
              delay={650}
              decimals={0}
              prefix={playerData.stats?.elo_change_30d && playerData.stats.elo_change_30d >= 0 ? '+' : ''}
            />
          </div>
        </motion.div>

        {/* Overall Win Rate Card - Full width on mobile, compact on desktop */}
        <motion.div
          className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg col-span-2 lg:col-span-1 transition-colors duration-300"
          initial={{ opacity: 0, scale: 0.9, y: 0 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.5, type: "spring", stiffness: 100 }}>
          <div className="text-center">
            <div className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-4 transition-colors duration-300">{t.overallWinRate}</div>
            <div className="relative inline-flex items-center justify-center mb-3">
              {/* Compact Circular Progress */}
              <svg className="w-28 h-28 transform -rotate-90">
                <circle
                  cx="56"
                  cy="56"
                  r="48"
                  stroke="currentColor"
                  className="text-gray-200 dark:text-gray-700"
                  strokeWidth="10"
                  fill="none"
                />
                <motion.circle
                  cx="56"
                  cy="56"
                  r="48"
                  stroke="currentColor"
                  className={
                    (stats.win_percentage || 0) >= 55
                      ? 'text-[#059669]'
                      : (stats.win_percentage || 0) >= 45
                      ? 'text-[#F59E0B]'
                      : 'text-[#C5283D]'
                  }
                  strokeWidth="10"
                  fill="none"
                  strokeDasharray={`${2 * Math.PI * 48}`}
                  strokeDashoffset={`${2 * Math.PI * 48}`}
                  strokeLinecap="round"
                  initial={{ strokeDashoffset: `${2 * Math.PI * 48}` }}
                  animate={{
                    strokeDashoffset: `${2 * Math.PI * 48 * (1 - (stats.win_percentage || 0) / 100)}`
                  }}
                  transition={{ duration: 1.5, ease: "easeOut" }}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className={`text-4xl font-bold ${
                  (stats.win_percentage || 0) >= 55
                    ? 'text-[#059669]'
                    : (stats.win_percentage || 0) >= 45
                    ? 'text-[#F59E0B]'
                    : 'text-[#C5283D]'
                }`}>
                  <CountingNumber target={stats.win_percentage || 0} duration={2} delay={900} decimals={0} suffix="%" />
                </div>
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 transition-colors duration-300">
              <CountingNumber target={stats.wins} duration={2} delay={900} decimals={0} />/
              <CountingNumber target={stats.total_games} duration={2} delay={900} decimals={0} />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Bottom Row - vs Higher and vs Lower - 2 columns on all screen sizes */}
      <div className="grid grid-cols-2 gap-4">
        {/* vs Higher ELO */}
        {playerData.stats?.vs_higher && (
          <motion.div
            className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg transition-colors duration-300"
            initial={{ opacity: 0, scale: 0.9, y: 0 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.6, type: "spring", stiffness: 100 }}>
            <div className="text-center">
              <div className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-6 transition-colors duration-300">{t.vsHigherElo}</div>
              <div className="relative inline-flex items-center justify-center mb-4">
                <svg className="w-32 h-32 transform -rotate-90">
                  <circle cx="64" cy="64" r="56" stroke="currentColor" className="text-gray-200 dark:text-gray-700" strokeWidth="12" fill="none" />
                  <motion.circle
                    cx="64" cy="64" r="56"
                    stroke="currentColor"
                    className={
                      (playerData.stats.vs_higher.percentage || 0) >= 40
                        ? 'text-[#059669]'
                        : (playerData.stats.vs_higher.percentage || 0) >= 30
                        ? 'text-[#F59E0B]'
                        : 'text-[#C5283D]'
                    }
                    strokeWidth="12" fill="none"
                    strokeDasharray={`${2 * Math.PI * 56}`}
                    strokeDashoffset={`${2 * Math.PI * 56}`}
                    strokeLinecap="round"
                    initial={{ strokeDashoffset: `${2 * Math.PI * 56}` }}
                    animate={{
                      strokeDashoffset: `${2 * Math.PI * 56 * (1 - (playerData.stats.vs_higher.percentage || 0) / 100)}`
                    }}
                    transition={{ duration: 1.5, ease: "easeOut" }}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className={`text-4xl font-bold ${
                    (playerData.stats.vs_higher.percentage || 0) >= 40
                      ? 'text-[#059669]'
                      : (playerData.stats.vs_higher.percentage || 0) >= 30
                      ? 'text-[#F59E0B]'
                      : 'text-[#C5283D]'
                  }`}>
                    <CountingNumber target={playerData.stats.vs_higher.percentage || 0} duration={2} delay={950} decimals={0} suffix="%" />
                  </div>
                </div>
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 transition-colors duration-300">
                (<CountingNumber target={playerData.stats.vs_higher.wins} duration={2} delay={950} decimals={0} />/
                <CountingNumber target={playerData.stats.vs_higher.total} duration={2} delay={950} decimals={0} />)
              </div>
            </div>
          </motion.div>
        )}

        {/* vs Lower ELO */}
        {playerData.stats?.vs_lower && (
          <motion.div
            className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg transition-colors duration-300"
            initial={{ opacity: 0, scale: 0.9, y: 0 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.7, type: "spring", stiffness: 100 }}>
            <div className="text-center">
              <div className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-6 transition-colors duration-300">{t.vsLowerElo}</div>
              <div className="relative inline-flex items-center justify-center mb-4">
                <svg className="w-32 h-32 transform -rotate-90">
                  <circle cx="64" cy="64" r="56" stroke="currentColor" className="text-gray-200 dark:text-gray-700" strokeWidth="12" fill="none" />
                  <motion.circle
                    cx="64" cy="64" r="56"
                    stroke="currentColor"
                    className={
                      (playerData.stats.vs_lower.percentage || 0) >= 70
                        ? 'text-[#059669]'
                        : (playerData.stats.vs_lower.percentage || 0) >= 55
                        ? 'text-[#F59E0B]'
                        : 'text-[#C5283D]'
                    }
                    strokeWidth="12" fill="none"
                    strokeDasharray={`${2 * Math.PI * 56}`}
                    strokeDashoffset={`${2 * Math.PI * 56}`}
                    strokeLinecap="round"
                    initial={{ strokeDashoffset: `${2 * Math.PI * 56}` }}
                    animate={{
                      strokeDashoffset: `${2 * Math.PI * 56 * (1 - (playerData.stats.vs_lower.percentage || 0) / 100)}`
                    }}
                    transition={{ duration: 1.5, ease: "easeOut" }}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className={`text-4xl font-bold ${
                    (playerData.stats.vs_lower.percentage || 0) >= 70
                      ? 'text-[#059669]'
                      : (playerData.stats.vs_lower.percentage || 0) >= 55
                      ? 'text-[#F59E0B]'
                      : 'text-[#C5283D]'
                  }`}>
                    <CountingNumber target={playerData.stats.vs_lower.percentage || 0} duration={2} delay={1000} decimals={0} suffix="%" />
                  </div>
                </div>
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 transition-colors duration-300">
                (<CountingNumber target={playerData.stats.vs_lower.wins} duration={2} delay={1000} decimals={0} />/
                <CountingNumber target={playerData.stats.vs_lower.total} duration={2} delay={1000} decimals={0} />)
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default PlayerPerformanceStats;
